/**
 * The application shell, read directly off the hero mockup in
 * landing/src/components/surfaces/ReportingBoard.astro.
 *
 * `bg-night` on the rail is doing real work: the design guide is explicit that the ink
 * rail is the single strongest cue that this is an application rather than page
 * furniture, and it is what keeps the surface inside the Ledger palette instead of
 * importing the default SaaS look.
 */
import type { ReactNode } from "react";
import type { IconName } from "@onecpa/tokens/icons";
import { CountPill } from "./Badge.js";
import { Icon } from "./Icon.js";
import { IconButton, Skeleton } from "./primitives.js";

// ---------------------------------------------------------------- page header

export interface PageHeaderProps {
  title: string;
  /** The period pill: tabular, hairline-bordered, sitting beside the title. */
  period?: ReactNode | undefined;
  subtitle?: string | undefined;
  /** Appears only at `xl`, per the mockup. */
  search?: ReactNode | undefined;
  notificationCount?: number | undefined;
  onNotifications?: (() => void) | undefined;
  /** The single primary action for this screen. */
  action?: ReactNode | undefined;
  children?: ReactNode | undefined;
}

export function PageHeader({
  title,
  period,
  subtitle,
  search,
  notificationCount,
  onNotifications,
  action,
  children,
}: PageHeaderProps) {
  return (
    <header className="border-b border-rule bg-surface">
      {/*
        items-start, not items-center. With a subtitle the title block is two
        lines, and centring the whole row floated the period pill halfway down
        it instead of lining up with the title. The pill is grouped with the
        title in its own row so it stays on the first line either way.
      */}
      <div className="flex flex-wrap items-start gap-3 px-5 py-3">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-lg font-bold text-ink">{title}</h1>
            {period && (
              <span className="tnum inline-flex h-5 shrink-0 items-center rounded-pill border border-rule bg-paper px-2.5 text-xs leading-none text-ink-muted">
                {period}
              </span>
            )}
          </div>
          {subtitle && <p className="mt-0.5 text-xs text-ink-muted">{subtitle}</p>}
        </div>

        <div className="ms-auto flex items-center gap-2">
          {search && <div className="hidden xl:block">{search}</div>}

          {onNotifications && (
            <span className="relative">
              <IconButton icon="bell" label="התראות" onClick={onNotifications} />
              {notificationCount !== undefined && notificationCount > 0 && (
                <CountPill
                  count={notificationCount}
                  tone="danger"
                  className="absolute -end-0.5 -top-0.5"
                />
              )}
            </span>
          )}

          {action}
        </div>
      </div>
      {children}
    </header>
  );
}

// ------------------------------------------------------------------ tab strip

export interface Tab {
  id: string;
  label: string;
  count?: number | undefined;
}

export function TabStrip({
  tabs,
  active,
  onSelect,
}: {
  tabs: Tab[];
  active: string;
  onSelect(id: string): void;
}) {
  return (
    <div className="flex gap-4 overflow-x-auto border-t border-rule px-5" role="tablist">
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(tab.id)}
            className={[
              // -mb-px pulls the active underline onto the container's own border,
              // so the two hairlines read as one.
              "-mb-px flex items-center gap-1.5 border-b-2 py-2 text-sm whitespace-nowrap transition-colors",
              "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ledger",
              isActive
                ? "border-ledger font-semibold text-ink"
                : "border-transparent text-ink-muted hover:text-ink",
            ].join(" ")}
          >
            {tab.label}
            {/* undefined means "not counted yet" and shows nothing, rather than a
                zero that reads as a real, empty result. */}
            {tab.count !== undefined && (
              <CountPill count={tab.count} tone={isActive ? "accent" : "neutral"} />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ KPI strip

export type KpiTone = "ink" | "ledger" | "pending" | "ochre" | "red";

export interface Kpi {
  label: string;
  /**
   * `undefined` means "not known yet" and renders a skeleton.
   *
   * This is the whole fix for figures that flashed `0` before settling: screens
   * used to write `data?.total ?? 0`, so the first paint asserted a real, empty
   * result. Leaving the fallback off makes the loading state the honest default.
   */
  value?: ReactNode | undefined;
  tone?: KpiTone | undefined;
  /** Optional drill-through. A KPI you cannot open is a poster. */
  onClick?: (() => void) | undefined;
}

const KPI_TONES: Record<KpiTone, string> = {
  ink: "text-ink",
  ledger: "text-ledger",
  pending: "text-pending-ink",
  ochre: "text-ochre",
  red: "text-red-ink",
};

/**
 * The five-column hairline-divided strip.
 *
 * Separation is a per-cell inline-start border rather than `divide-x`. The grid
 * wraps — two columns on a phone, three at `md`, five at `lg` — and `divide-x`
 * only understands a single row, so with five items the rules landed on the wrong
 * edges of the wrapped row. A border the cell owns is correct at every width, and
 * `border-s` already flips for RTL without a `divide-x-reverse` counterpart.
 *
 * `loading` renders every cell as a skeleton. Individual cells also skeleton
 * themselves when their value is `undefined`, so a strip can be partly resolved
 * without the screen having to gate the whole thing.
 */
export function KpiStrip({ items, loading = false }: { items: Kpi[]; loading?: boolean }) {
  return (
    <div
      className="grid grid-cols-2 border-b border-rule bg-surface md:grid-cols-3 lg:grid-cols-5"
      aria-busy={loading || undefined}
    >
      {items.map((item, index) => {
        const pending = loading || item.value === undefined;

        const content = (
          <>
            <span className="block text-2xs text-ink-faint">{item.label}</span>
            {pending ? (
              // Sized to the figure it replaces, so the strip does not resize
              // when the number lands.
              <Skeleton className="mt-1 h-6 w-16" />
            ) : (
              <span
                className={`serif tnum mt-0.5 block text-2xl ${KPI_TONES[item.tone ?? "ink"]}`}
              >
                {item.value}
              </span>
            )}
          </>
        );

        // No rule before the first cell of any row; the container's own border
        // closes the strip.
        const divider = [
          "border-s border-rule",
          index % 2 === 0 ? "border-s-0" : "",
          index % 3 === 0 ? "md:border-s-0" : "md:border-s",
          index % 5 === 0 ? "lg:border-s-0" : "lg:border-s",
        ].join(" ");

        return item.onClick ? (
          <button
            key={item.label}
            type="button"
            onClick={item.onClick}
            className={`px-4 py-3 text-start transition-colors hover:bg-wash/50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ledger ${divider}`}
          >
            {content}
          </button>
        ) : (
          <div key={item.label} className={`px-4 py-3 ${divider}`}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------- nav rail

export interface NavEntry {
  id: string;
  icon: IconName;
  label: string;
  /** Hidden when the caller's role is below this. Mirrors the server's gate. */
  roles?: string[] | undefined;
  badge?: number | undefined;
}

export interface NavGroup {
  label: string;
  entries: NavEntry[];
}

/** The active tenant, as the rail needs to draw it. */
export interface FirmIdentity {
  name: string;
  logoUrl?: string | null | undefined;
  monogram?: string | null | undefined;
}

export interface NavRailProps {
  groups: NavGroup[];
  role: string;
  activeId: string;
  onNavigate(id: string): void;
  userName: string;
  roleLabel: string;
  onSignOut(): void;
  /**
   * Which firm's data is on screen.
   *
   * Not optional in practice: without it the rail shows the OneCPA wordmark and
   * nothing else, so somebody who serves two firms cannot tell which tenant they
   * are looking at — and the answer decides whether what they are about to change
   * is the right client's record.
   */
  firm?: FirmIdentity | undefined;
  /** Firm switcher, when the user belongs to more than one. */
  footer?: ReactNode | undefined;
  /**
   * The light/dark control, sitting beside sign-out. Passed in rather than built
   * here because the preference is app state, not shell state.
   */
  themeControl?: ReactNode | undefined;
  collapsed?: boolean | undefined;
  onToggleCollapsed?: (() => void) | undefined;
}

/**
 * The firm's mark, in two shapes.
 *
 * `wordmark` lets the logo keep its own aspect ratio and take the width it needs.
 * A firm logo is almost always wider than it is tall — the supplied one is
 * 160×61 — and forcing it into a square box shrinks it to the height of the
 * square, which at rail scale renders it as an unreadable smudge.
 *
 * `tile` is a fixed square carrying the monogram, for the two places a wordmark
 * cannot work: the collapsed rail, which is 56px wide in total, and any row where
 * marks from several firms must line up.
 *
 * Logos are white-on-transparent by convention because the rail is `bg-night`;
 * one with its own background would punch a light rectangle into the ink column
 * that the design guide calls the application's strongest signal. The monogram
 * also covers a logo that fails to load, which on a short-lived URL is a matter
 * of when rather than if.
 */
export function FirmMark({
  firm,
  variant = "tile",
  size = "md",
}: {
  firm: FirmIdentity;
  variant?: "tile" | "wordmark" | undefined;
  size?: "sm" | "md" | undefined;
}) {
  if (variant === "wordmark" && firm.logoUrl) {
    return (
      <img
        src={firm.logoUrl}
        alt=""
        // The name is rendered beside or below it; announcing the logo too would
        // read the firm twice.
        aria-hidden="true"
        // Width-led with a height cap, not a fixed height. A firm mark is a
        // roundel plus two lines of type, and at the 24px a fixed height gave it
        // the type was too small to read — which made five of the six firms look
        // like placeholders next to the one real logo.
        className={[
          "h-auto w-full object-contain",
          size === "sm" ? "max-h-8" : "max-h-12",
          // The rail is RTL, so an image narrower than its box should sit against
          // the reading edge rather than drifting left.
          "object-right",
        ].join(" ")}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={[
        size === "sm" ? "size-6 text-2xs" : "size-7 text-2xs",
        "grid shrink-0 place-items-center rounded-card bg-white/12 font-bold text-on-ink",
      ].join(" ")}
    >
      {firm.monogram ?? firm.name.slice(0, 2)}
    </span>
  );
}

/**
 * The rail, in two widths.
 *
 * Collapsing hides the labels but never removes them — they stay in the DOM as `sr-only`,
 * so a screen reader still announces "לקוחות" rather than "button". A collapsed rail that
 * drops its text is a rail that only works for people who can see the icons, and with
 * sixteen destinations icon-only is already a memory test.
 *
 * Width is the only thing that animates, and `prefers-reduced-motion` is honoured globally
 * in tokens.css, so this stays inside the design guide's near-zero-motion policy without
 * special-casing anything here.
 */
export function NavRail({
  groups,
  role,
  activeId,
  onNavigate,
  userName,
  roleLabel,
  onSignOut,
  firm,
  footer,
  themeControl,
  collapsed = false,
  onToggleCollapsed,
}: NavRailProps) {
  return (
    <aside
      id="nav-rail"
      // h-full inside a shell that does not itself scroll. The rail is pinned by layout
      // rather than by `position: fixed`, so there is no content offset to keep in sync
      // with its width — which is what makes a collapsible rail safe rather than fiddly.
      className={[
        "hidden h-full shrink-0 flex-col bg-night transition-[width] duration-150 lg:flex",
        collapsed ? "w-14" : "w-44",
      ].join(" ")}
    >
      {/*
        Fixed height, and the toggle stays in flow in both states.

        It used to be `absolute top-3.5 end-1`, which put it on top of the mark
        that the collapsed 56px rail centres — two glyphs in one 24px square. It
        was also a text chevron (‹ / ›) rendered in the Hebrew UI face, so its
        weight and vertical centring matched nothing else in the product.

        Collapsed, the toggle is the only thing in the row and is centred on the
        same axis as the nav icons below it; the firm mark carries identity from
        the block underneath. `sidebar-simple` is symmetric, so unlike a chevron
        it needs no reasoning about which way it should point in RTL.
      */}
      <div
        className={`flex h-12 shrink-0 items-center border-b border-night-line ${
          collapsed ? "justify-center px-2" : "gap-2 px-4"
        }`}
      >
        {!collapsed && <Wordmark />}
        {onToggleCollapsed ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            // The name says what the control will do; aria-expanded says where it is now.
            // Both are needed — a label alone leaves the current state implicit.
            aria-expanded={!collapsed}
            aria-controls="nav-rail"
            aria-label={collapsed ? "הרחבת התפריט" : "כיווץ התפריט"}
            title={collapsed ? "הרחבת התפריט" : "כיווץ התפריט"}
            className={[
              "grid size-7 shrink-0 place-items-center rounded-field",
              "text-night-ink transition-colors hover:bg-white/8 hover:text-on-ink",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ledger-bright",
              collapsed ? "" : "ms-auto",
            ].join(" ")}
          >
            <Icon name="sidebar-simple" className="size-4" />
          </button>
        ) : (
          collapsed && <Mark />
        )}
      </div>

      {/* Whose data this is.
          
          Directly beneath the product wordmark and above everything else, because
          it qualifies every number on the screen: the same layout, the same
          columns and the same person's name, showing a different firm's clients.
          Collapsed, the mark alone remains and the name moves into its title. */}
      {firm && (
        <div
          className={`border-b border-night-line py-2.5 ${collapsed ? "px-2" : "px-4"}`}
          title={firm.name}
        >
          {collapsed ? (
            <span className="flex justify-center">
              <FirmMark firm={firm} variant="tile" size="sm" />
              {/* No visible name at this width, so the whole announcement is
                  hidden text rather than a prefix to something on screen. */}
              <span className="sr-only">המשרד הפעיל: {firm.name}</span>
            </span>
          ) : (
            <>
              {/* The logo gets the full rail width and its own aspect ratio. A
                  wordmark squeezed into a square is the height of the square,
                  which at this scale is a smudge. */}
              {firm.logoUrl ? (
                <FirmMark firm={firm} variant="wordmark" />
              ) : (
                <FirmMark firm={firm} variant="tile" />
              )}
              {/* Only the label is hidden. Repeating the name in a second sr-only
                  element would announce the firm twice and, less obviously, make
                  every "find the firm name" query match two nodes. */}
              <span className="mt-1.5 block truncate text-2xs text-night-ink">
                <span className="sr-only">המשרד הפעיל: </span>
                {firm.name}
              </span>
            </>
          )}
        </div>
      )}

      {/* The rail scrolls independently of the content, and only when it has to: sixteen
          destinations fit on a laptop, and a shorter viewport gets a scrollbar here rather
          than pushing the user chip off the bottom. */}
      <nav className="flex-1 overflow-y-auto py-2" aria-label="ניווט ראשי">
        {groups.map((group) => {
          const visible = group.entries.filter(
            (entry) => !entry.roles || entry.roles.includes(role),
          );
          if (visible.length === 0) return null;

          return (
            <div key={group.label} className="mb-1">
              {collapsed ? (
                // The group label cannot be read at this width, so grouping is carried by a
                // hairline instead: the structure stays perceivable without the words.
                <hr className="mx-3 my-2 border-night-line" aria-hidden="true" />
              ) : (
                <p className="px-4 pt-2 pb-1 text-2xs font-semibold text-night-ink/60">
                  {group.label}
                </p>
              )}
              {visible.map((entry) => {
                const active = entry.id === activeId;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    aria-current={active ? "page" : undefined}
                    onClick={() => onNavigate(entry.id)}
                    title={collapsed ? entry.label : undefined}
                    // border-s-2 flips correctly in RTL; the mockup's active indicator sits
                    // on the inline-start edge.
                    className={[
                      "flex w-full items-center border-s-2 py-2 text-start text-sm transition-colors",
                      collapsed ? "justify-center px-0" : "gap-2.5 px-4",
                      "focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-ledger-bright",
                      active
                        ? "border-ledger-bright bg-white/8 font-semibold text-on-ink"
                        : "border-transparent text-night-ink hover:bg-white/5",
                    ].join(" ")}
                  >
                    <span className="relative shrink-0">
                      <Icon
                        name={entry.icon}
                        weight={active ? "fill" : "regular"}
                        className={`size-4 ${active ? "text-ledger-bright" : "opacity-70"}`}
                      />
                      {/* Collapsed, a count has nowhere to sit inline, so it becomes a dot on
                          the icon. The number itself stays in the accessible label. */}
                      {collapsed && entry.badge !== undefined && entry.badge > 0 && (
                        <span
                          className="absolute -end-1 -top-1 size-1.5 rounded-full bg-ledger-bright"
                          aria-hidden="true"
                        />
                      )}
                    </span>

                    {/* Hidden, never removed: the accessible name must survive the collapse. */}
                    <span className={collapsed ? "sr-only" : "min-w-0 flex-1 truncate"}>
                      {entry.label}
                      {collapsed && entry.badge !== undefined && entry.badge > 0
                        ? ` (${entry.badge})`
                        : ""}
                    </span>

                    {!collapsed && entry.badge !== undefined && entry.badge > 0 && (
                      <CountPill count={entry.badge} tone="chrome" />
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* The firm switcher is a select with a visible label, so it has no useful collapsed
          form. Collapsing hides it; the firm name is still in the mobile bar and the
          dashboard subtitle. */}
      {!collapsed && footer}

      <div
        className={`flex items-center border-t border-night-line py-3 ${
          collapsed ? "flex-col gap-2 px-2" : "gap-2.5 px-4"
        }`}
      >
        <span
          className="grid size-7 shrink-0 place-items-center rounded-full bg-ledger text-xs font-bold text-on-primary"
          title={collapsed ? `${userName} · ${roleLabel}` : undefined}
        >
          {userName.charAt(0)}
        </span>
        {collapsed ? (
          <span className="sr-only">
            {userName} · {roleLabel}
          </span>
        ) : (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-semibold text-on-ink">
              {userName}
            </span>
            <span className="block truncate text-2xs text-night-ink/80">{roleLabel}</span>
          </span>
        )}
        {themeControl}
        <button
          type="button"
          onClick={onSignOut}
          className="text-night-ink transition-colors hover:text-on-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ledger-bright"
          aria-label="יציאה"
          title="יציאה"
        >
          <Icon name="log-out" className="size-4" />
        </button>
      </div>
    </aside>
  );
}

/** The wordmark's glyph alone, for the collapsed rail. */
function Mark() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-5"
      fill="none"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <g stroke="#edeae0" strokeWidth={1.7} opacity={0.45}>
        <path d="M13 6h8" />
        <path d="M8 11h13" />
      </g>
      <path d="M3 18h18" stroke="#23a06b" strokeWidth={3} />
    </svg>
  );
}

/**
 * The product wordmark, in the two grounds it appears on.
 *
 * `light` is for the ink rail, `dark` for paper. Both are needed because the
 * ruled glyph is drawn in a near-white that disappears entirely on the portal's
 * warm paper — which is how the footer credit came out as a lone green dash. The
 * sign-in screen had worked around the same problem by hand-rolling its own dark
 * copy of this SVG, which is a second wordmark to keep in step with the first.
 */
export function Wordmark({
  className = "",
  tone = "light",
}: {
  className?: string;
  tone?: "light" | "dark" | undefined;
}) {
  const rule = tone === "light" ? "#edeae0" : "#101a15";
  const accent = tone === "light" ? "#23a06b" : "#136f4a";

  return (
    <span className={`flex items-center gap-2 ${className}`}>
      <svg
        viewBox="0 0 24 24"
        className="size-5 shrink-0"
        fill="none"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <g stroke={rule} strokeWidth={1.7} opacity={tone === "light" ? 0.45 : 0.35}>
          <path d="M13 6h8" />
          <path d="M8 11h13" />
        </g>
        <path d="M3 18h18" stroke={accent} strokeWidth={3} />
      </svg>
      <span
        className={`serif text-md font-bold ${tone === "light" ? "text-on-ink" : "text-ink"}`}
        dir="ltr"
      >
        One<span className={tone === "light" ? "text-ledger-bright" : "text-ledger"}>CPA</span>
      </span>
    </span>
  );
}

/**
 * The phone composition: a separate layout, not a collapsed desktop one.
 *
 * The design guide is explicit about why. Dropping columns from the desktop table
 * "left a masthead and four numbers" — so the phone gets an ink top bar, a period row
 * and a bottom tab bar of its own, and the tables become list rows.
 */
export function MobileBar({
  title,
  entries,
  activeId,
  onNavigate,
  onMenu,
  firm,
}: {
  title: string;
  entries: NavEntry[];
  activeId: string;
  onNavigate(id: string): void;
  onMenu?: () => void;
  firm?: FirmIdentity | undefined;
}) {
  return (
    <>
      {/* shrink-0 because the shell column has overflow-hidden: without it a tall screen
          would compress the bar instead of scrolling the content beneath it. */}
      <div className="flex shrink-0 items-center gap-2.5 bg-night px-4 py-2.5 lg:hidden">
        {onMenu && (
          <button type="button" onClick={onMenu} className="text-night-ink" aria-label="תפריט">
            <Icon name="menu" className="size-5" />
          </button>
        )}
        {/* The firm's mark rather than the product's: on a phone this bar is the
            only place the tenant appears at all, and there is no room for both. */}
        {firm && <FirmMark firm={firm} size="sm" />}
        <span className="truncate text-base font-semibold text-on-ink">{title}</span>
        <span className="ms-auto">
          <Wordmark />
        </span>
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-night-line bg-night lg:hidden"
        aria-label="ניווט"
      >
        {entries.slice(0, 5).map((entry) => {
          const active = entry.id === activeId;
          return (
            <button
              key={entry.id}
              type="button"
              onClick={() => onNavigate(entry.id)}
              aria-current={active ? "page" : undefined}
              className="flex flex-1 flex-col items-center gap-0.5 py-2"
            >
              <Icon
                name={entry.icon}
                weight={active ? "fill" : "regular"}
                className={`size-5 ${active ? "text-ledger-bright" : "text-night-ink opacity-70"}`}
              />
              <span
                className={`text-2xs ${active ? "font-semibold text-on-ink" : "text-night-ink"}`}
              >
                {entry.label}
              </span>
            </button>
          );
        })}
      </nav>
    </>
  );
}

/** The frame: ink rail, paper content, and room for the mobile tab bar. */
export function AppShell({ rail, children }: { rail: ReactNode; children: ReactNode }) {
  return (
    // h-dvh with overflow-hidden: the document itself never scrolls, so the rail is pinned
    // by layout rather than by `position: fixed`.
    //
    // The alternative — a fixed rail plus a matching inline-start offset on the content —
    // needs the rail's width duplicated as a magic number in two places, and it stops being
    // one number the moment the rail can collapse. This version has no offset at all: it is
    // ordinary flex, so it behaves identically in RTL and the width can change freely.
    //
    // The consequence to know about: the scroll container is now the content column, not the
    // page. `sticky top-0` on a table header therefore sticks to the top of that column,
    // which is what the ruled table wants.
    <div className="flex h-dvh overflow-hidden bg-paper">
      {/* Keyboard users would otherwise tab through sixteen rail destinations before
          reaching the screen. Visible only on focus, and first in the tab order. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-50 focus:rounded-card focus:bg-ledger focus:px-3 focus:py-1.5 focus:text-sm focus:font-semibold focus:text-on-primary"
      >
        דלג לתוכן
      </a>
      {rail}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-surface">{children}</div>
    </div>
  );
}

/**
 * The scrolling region.
 *
 * Separate from AppShell so a screen's persistent chrome — the mobile top bar, the running
 * timer — can sit outside it and stay visible while the content scrolls underneath.
 * `pb-14` clears the phone's fixed bottom tab bar.
 */
export function ShellContent({ children }: { children: ReactNode }) {
  return (
    <main id="main" className="flex-1 overflow-y-auto pb-14 lg:pb-0">
      {children}
    </main>
  );
}
